import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { getAdvanceLeaves, updateLeaveStatus } from '../../../Redux/Slice/LeaveForm';
import { format, isSameDay, addDays, getDay, isSameMonth, isSameYear, subMonths } from 'date-fns';
import { FiChevronDown, FiChevronUp, FiPhoneCall } from 'react-icons/fi';
import Loader from '../../../Components/Loader/Loader';
import { TablePagination } from "@mui/material";
import socket from '../../../socket';

const OwnerInbox = () => {
    const dispatch = useDispatch();

    const loginDataStr = Cookies.get("loginData");
    let loginData = null;
    if (loginDataStr) {
        loginData = JSON.parse(loginDataStr);
    }
    const applicantUser = loginData?.data || loginData?.user || {};

    const { leaveData, listLoading, error } = useSelector((state) => state.LeaveForm);
    const [expandedId, setExpandedId] = useState(null);
    const [actionRemark, setActionRemark] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const [actionModal, setActionModal] = useState({ isOpen: false, id: null, actionType: null });
    const [modalError, setModalError] = useState('');

    useEffect(() => {

        socket.on("leaveUpdate", (data) => {
            console.log("Live leave update received:", data);
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        });

        return () => {
            socket.off("leaveUpdate");
        };
    }, [dispatch, applicantUser?._id]);


    useEffect(() => {
        if (applicantUser?._id) {
            dispatch(getAdvanceLeaves(applicantUser._id));
        }
    }, [dispatch, applicantUser?._id]);

    const handleAction = (id, action) => {
        setModalError('');
        setActionModal({ isOpen: true, id, actionType: action });
    };

    const confirmAction = async () => {
        const { id, actionType } = actionModal;

        if (actionType === 'Reject' && !actionRemark.trim()) {
            setModalError('Comment / Remark is mandatory for Reject. Please cancel, write a remark, and try again.');
            return;
        }

        if (id && actionType) {
            await dispatch(updateLeaveStatus({
                id,
                action: actionType,
                remark: actionRemark,
                name: applicantUser?.name || applicantUser?.Name || 'Owner',
                approverId: applicantUser?._id,
                position: applicantUser?.position || applicantUser?.Role || 'Owner'
            }));
            setExpandedId(null);
            setActionRemark('');
            if (applicantUser?._id) {
                dispatch(getAdvanceLeaves(applicantUser._id));
            }
        }
        setActionModal({ isOpen: false, id: null, actionType: null });
        setModalError('');
    };

    const cancelAction = () => {
        setActionModal({ isOpen: false, id: null, actionType: null });
        setModalError('');
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
        setActionRemark(''); // Reset remark when switching tabs
    };

    const processedLeaves = leaveData?.map(leave => {
        const userApprover = leave.approvers?.find(app => String(app.approverId) === String(applicantUser?._id));
        const userStatus = userApprover ? userApprover.status : leave.Status;
        const isCurrentApprover = leave.approvers &&
            leave.currentApprovalStep < leave.approvers.length &&
            String(leave.approvers[leave.currentApprovalStep].approverId) === String(applicantUser?._id);

        return {
            ...leave,
            userStatus,
            isCurrentApprover,
            myApproverData: userApprover
        };
    }) || [];

    const filteredLeaves = processedLeaves.filter(leave => {
        if (leave.Status === 'Withdrawn') return false;

        // Dynamically detect if current user is an owner
        const isUserOwner = applicantUser?.position?.toLowerCase() === 'owner' ||
            applicantUser?.Role?.toLowerCase() === 'owner';
        // Check if there is any owner in the approval chain
        const hasOwnerInPath = leave.approvers?.some(app => app.position && app.position.toLowerCase() === 'owner');

        if (isUserOwner && hasOwnerInPath) {
            // Owners see all pending requests that are destined for an owner
            return leave.Status === 'Pending';
        }

        if (leave.myApproverData) {
            // Normal sequential fallback for non-owners or specific logic:
            if (leave.myApproverData.status === 'Pending' && !leave.isCurrentApprover) {
                return false;
            }
        }
        return true;
    });

    const pendingRequests = filteredLeaves.filter(l => l.userStatus === 'Pending');

    const calculateWorkingDays = (startDate, endDate) => {
        if (!startDate || !endDate) return 0;
        let count = 0;
        let curr = new Date(startDate);
        const end = new Date(endDate);
        while (curr <= end) {
            if (getDay(curr) !== 0) { // 0 is Sunday
                count++;
            }
            curr = addDays(curr, 1);
        }
        return count;
    };

    const getApprovedCountForMonth = (applicantName, targetMoment) => {
        const approvedLeaves = leaveData?.filter(
            l => l.Status === 'Approved' && l.applicantName === applicantName
        );
        if (!approvedLeaves) return 0;

        let totalLeaves = 0;
        approvedLeaves.forEach(l => {
            if (!l.fromDate) return;
            const start = new Date(l.fromDate);
            if (isSameMonth(start, targetMoment) && isSameYear(start, targetMoment)) {
                totalLeaves += 1;
            }
        });
        return totalLeaves;
    };

    if (listLoading) {
        return <Loader />;
    }

    return (
        <div className="px-3 py-6 min-h-0 bg-gradient-to-br from-slate-50/50 via-white to-slate-50/20 font-sans">
            <div className="w-full space-y-6">
                {/* Page Title */}
                <div className="title mb-4">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Approval & Leave Queue
                    </h3>
                </div>

                <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 px-6 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-[15px] font-bold text-gray-900 tracking-tight">Management Inbox</h1>
                        <p className="text-xs text-gray-600 font-semibold uppercase tracking-[0.1em] mt-0.5">Reviewing pending operations</p>
                    </div>
                </div>

                {error && typeof error === 'string' && (
                    <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-3 border border-red-200">
                        <p className="font-medium text-sm">{error}</p>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto transition-all">
                    {/* Header Desktop */}
                    <div className="hidden md:grid grid-cols-12 gap-4 p-5 px-8 border-b border-[#133A69]/10 bg-[#133A69] text-[11px] font-bold text-blue-50 tracking-wider uppercase">
                        <div className="col-span-3">Employee Name</div>
                        <div className="col-span-2">Designation</div>
                        <div className="col-span-2">Leave Type</div>
                        <div className="col-span-2">Duration</div>
                        <div className="col-span-2">Status</div>
                        <div className="col-span-1 text-right">Actions</div>
                    </div>

                    {pendingRequests.length === 0 ? (
                        <div className="flex flex-col justify-center items-center py-20 bg-white">
                            <h3 className="text-gray-400 font-bold text-lg uppercase tracking-widest">Inbox Empty</h3>
                            <p className="text-gray-300 mt-1 text-sm italic underline decoration-gray-200">No pending approvals remaining.</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-50 bg-white">
                            {pendingRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((leave) => {
                                const isExpanded = expandedId === leave._id;
                                const shortId = leave._id ? `LR${leave._id.slice(-4).toUpperCase()}` : 'LR0001';

                                return (
                                    <React.Fragment key={leave._id}>
                                        {/* Main Row */}
                                        <div
                                            onClick={() => toggleExpand(leave._id)}
                                            className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 p-5 px-8 cursor-pointer transition-all items-center ${isExpanded ? 'bg-blue-50/50 border-l-4 border-blue-500' : 'hover:bg-gray-50/80 bg-white'}`}
                                        >
                                            {/* Mobile View Wrap */}
                                            <div className="flex justify-between items-center md:hidden w-full">
                                                <div>
                                                    <h3 className="text-[14px] font-semibold text-gray-900">{leave.applicantName || '-'}</h3>
                                                    <p className="text-[11px] text-gray-500 mt-0.5">{shortId}</p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold rounded-md">Pending</span>
                                                    {isExpanded ? <FiChevronUp className="text-blue-600" /> : <FiChevronDown className="text-blue-600" />}
                                                </div>
                                            </div>

                                            {/* Desktop Detailed Columns */}
                                            <div className="hidden md:block col-span-3">
                                                <h3 className="text-[14px] font-bold text-gray-900 tracking-tight">{leave.applicantName || '-'}</h3>
                                                <p className="text-[11px] text-gray-400 font-medium mt-0.5 italic">{shortId}</p>
                                            </div>

                                            <div className="hidden md:block col-span-2">
                                                <p className="text-[12px] text-gray-600 font-medium">{leave.applicantRole || '-'}</p>
                                            </div>

                                            <div className="hidden md:block col-span-2">
                                                <p className="text-[13px] text-blue-600 font-bold">{leave.LeaveType || 'Leave'}</p>
                                            </div>

                                            <div className="hidden md:block col-span-2">
                                                <p className="text-[14px] text-gray-800 font-semibold tracking-tight">
                                                    {isSameDay(new Date(leave.fromDate), new Date(leave.toDate))
                                                        ? format(new Date(leave.fromDate), 'dd MMM yyyy')
                                                        : `${format(new Date(leave.fromDate), 'dd MMM')} - ${format(new Date(leave.toDate), 'dd MMM yyyy')}`}
                                                </p>
                                                <p className="text-[11px] text-gray-400 font-medium mt-0.5 italic">
                                                    {leave.LeaveType === "Early Leave" ? (
                                                        `Time: ${leave.leavingTime}`
                                                    ) : leave.LeaveType === "Late Arrival" ? (
                                                        `Time: ${leave.arrivalTime}`
                                                    ) : leave.LeaveType === "Time Break" ? (
                                                        `Span: ${leave.leavingTime} - ${leave.arrivalTime}`
                                                    ) : (
                                                        `${leave.TotalDays || calculateWorkingDays(leave.fromDate, leave.toDate)} Days ${leave.halfType ? `(${leave.halfType})` : ''}`
                                                    )}
                                                </p>
                                            </div>

                                            <div className="hidden md:flex col-span-2 items-center">
                                                <span className="px-3 py-1 bg-amber-50 text-amber-600 border border-amber-100 text-[10px] font-bold rounded shadow-sm">
                                                    Pending Approval
                                                </span>
                                            </div>

                                            <div className="hidden md:flex col-span-1 justify-end items-center pr-2">
                                                <button className="text-blue-600 focus:outline-none p-2 bg-blue-50/50 hover:bg-blue-100 rounded-lg transition-colors">
                                                    {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Expanded Panel */}
                                        {isExpanded && (
                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8 px-8 md:px-12 bg-gray-50/30 border-t border-b border-gray-100 italicAnimation">
                                                {/* Left Column: Request Details */}
                                                <div>
                                                    <h4 className="text-[15px] font-bold text-gray-800 mb-5">Request Overview</h4>
                                                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                                                        <div className="grid grid-cols-2 gap-6">
                                                            <div>
                                                                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">Applied Date</p>
                                                                <p className="text-[13px] font-bold text-gray-800">{format(new Date(leave.createdAt), 'dd MMM yyyy')}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">Impact Type</p>
                                                                <p className="text-[13px] font-bold text-blue-600">{leave.LeaveType}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">Start Date</p>
                                                                <p className="text-[13px] font-bold text-gray-800">{format(new Date(leave.fromDate), 'dd MMM yyyy')}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-1">End Date</p>
                                                                <p className="text-[13px] font-bold text-gray-800">{format(new Date(leave.toDate), 'dd MMM yyyy')}</p>
                                                            </div>
                                                        </div>

                                                        {leave.LeaveType === "Emergency Leave" && (
                                                            <div className="mt-8 p-4 bg-red-50 rounded-xl border border-red-100">
                                                                <p className="text-[11px] text-red-400 font-bold uppercase mb-1">Emergency Context</p>
                                                                <p className="text-[14px] font-bold text-red-600">{leave.leaveReason}</p>
                                                            </div>
                                                        )}

                                                        <div className="mt-8">
                                                            <p className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-2">Reason provided by employee</p>
                                                            <p className="text-[14px] text-gray-700 italic bg-gray-50/50 p-4 rounded-xl border border-gray-50 leading-relaxed font-medium">
                                                                "{leave.reason?.split('\n')[0] || "No additional comments provided."}"
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right Column: Actions */}
                                                <div>
                                                    <h4 className="text-[15px] font-bold text-gray-800 mb-5">Decision & Remarks</h4>
                                                    
                                                    <div className="mb-6">
                                                        <label className="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-2 block">
                                                            Comment / Remark <span className="text-gray-300 italic font-medium ml-1">(Mandatory for Reject)</span>
                                                        </label>
                                                        <textarea
                                                            value={actionRemark}
                                                            onChange={(e) => setActionRemark(e.target.value)}
                                                            placeholder="State the reason for your decision..."
                                                            className="w-full h-[120px] p-4 text-[13px] border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 shadow-sm resize-none transition-all placeholder-gray-300 font-medium"
                                                        />
                                                    </div>

                                                    <div className="flex gap-4">
                                                        <button
                                                            onClick={() => handleAction(leave._id, 'Reject')}
                                                            className="flex-1 bg-white border-2 border-red-100 text-red-600 hover:bg-red-50 font-bold text-[13px] py-4 rounded-xl transition-all shadow-sm uppercase tracking-widest">
                                                            Reject
                                                        </button>
                                                        <button
                                                            onClick={() => handleAction(leave._id, 'Approve')}
                                                            className="flex-[2] bg-[#111827] hover:bg-black text-white font-bold text-[13px] py-4 rounded-xl transition-all shadow-lg active:scale-95 uppercase tracking-widest">
                                                            Approve
                                                        </button>
                                                    </div>

                                                    <p className="mt-5 text-[11px] text-gray-400 font-medium text-center italic">
                                                        Approval history and logs will be updated automatically.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            <TablePagination
                                component="div"
                                count={pendingRequests.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                className="bg-white border-t border-gray-100 placeholder:text-gray-500 rounded-b-2xl"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Action Confirmation Modal */}
            {actionModal.isOpen && (
                <div className="fixed inset-0 bg-gray-900/40 z-[100] flex items-center justify-center p-4 backdrop-blur-[2px]">
                    <div className="bg-white rounded-3xl p-8 max-w-[400px] w-full shadow-2xl animate-in zoom-in-95 duration-200">
                        <h3 className="text-[22px] font-black text-gray-900 mb-3 tracking-tight">
                            {actionModal.actionType === 'Approve' ? 'Approve Request?' : 'Reject Request?'}
                        </h3>

                        <p className="text-[14px] text-gray-500 mb-8 font-medium leading-relaxed">
                            Are you certain you want to {actionModal.actionType?.toLowerCase()} this leave application? This action cannot be undone.
                            {modalError && (
                                <span className="block mt-4 text-red-600 font-bold text-[12px] bg-red-50 p-3 rounded-lg border border-red-100 leading-relaxed shadow-sm italic">
                                    {modalError}
                                </span>
                            )}
                        </p>

                        <div className="flex gap-4">
                            <button
                                onClick={cancelAction}
                                className="flex-1 bg-gray-50 text-gray-400 font-bold py-3.5 rounded-xl hover:bg-gray-100 transition-colors text-[13px] uppercase tracking-widest"
                            >
                                Back
                            </button>
                            <button
                                onClick={confirmAction}
                                className={`flex-[2] text-white font-bold py-3.5 rounded-xl transition-all text-[13px] shadow-lg active:scale-95 uppercase tracking-widest ${actionModal.actionType === 'Approve' ? 'bg-[#05a557] hover:bg-[#04914c]' : 'bg-[#dc2626] hover:bg-[#b91c1c]'}`}
                            >
                                Yes, Confirm {actionModal.actionType}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OwnerInbox;